import React from "react";
import { StoreCategory } from "tsi-common-react/src/api/retail";
import {
    MarkerMap,
    type MarkerMapHandle,
} from "tsi-common-react/src/apps/retail/containers/MarkerMap";
import { RetailStoreEventType } from "tsi-common-react/src/constants";
import { IRetailStoreWithDistance } from "tsi-common-react/src/models/location";
import { ISyncStoreID } from "tsi-common-react/src/models/nominals";
import { trackRetailStoreEvent } from "tsi-common-react/src/utils/analytics";
import { t } from "ttag";

import { LocatorNearestStoresRow } from "../components/LocatorNearestStoresRow";
import { groupStores } from "../utils";
import { LocatorSendResultsEmail } from "./LocatorSendResults_Email";
import { LocatorSendResultsPrint } from "./LocatorSendResults_Print";
import { LocatorSendResultsText } from "./LocatorSendResults_Text";
import { NoResults } from "./NoResults";

const getStoreNumber = (
    stores: ReadonlyArray<IRetailStoreWithDistance>,
    store: IRetailStoreWithDistance,
): number => {
    return stores.indexOf(store) + 1;
};

const createInfoWindowContent = (store: IRetailStoreWithDistance): string => {
    const url = `/locations/states/${store.state}/cities/${store.city}/stores/${store.id}/`;
    // TODO(i18n): The InfoWindow HTML structure should be created using React/JSX with proper i18n support instead of template strings. Consider refactoring to use a React component that can be rendered to HTML string with t template tags for any static text labels if needed.
    return `
        <div class="loc__info-window">
            <div class="loc-name">
                <a href="${url}" target="_blank" rel="noopener noreferrer" title=${store.name}>
                    ${store.name}
                </a>
            </div>
            <div class="loc-content">
                <div class="loc-address">${store.address}</div>
            </div>
        </div>
    `;
};

const trackStoreAnalytics = (
    storeGroup: {
        main: IRetailStoreWithDistance;
        secondary: ReadonlyArray<IRetailStoreWithDistance>;
    },
    storeID: ISyncStoreID,
): void => {
    const store =
        storeGroup.main.external_id === storeID
            ? storeGroup.main
            : storeGroup.secondary.find((s) => s.external_id === storeID);
    if (store) {
        trackRetailStoreEvent(
            store.data?.classification_tempur === StoreCategory.FLAGSHIP
                ? RetailStoreEventType.TEMPUR_STORE
                : RetailStoreEventType.RETAIL_STORE,
            store,
        );
    }
};

const createMarkerOptions = (
    stores: ReadonlyArray<IRetailStoreWithDistance>,
    store: IRetailStoreWithDistance,
): google.maps.MarkerOptions => {
    return {
        icon: {
            path: `M12.964,0.542c-6.895,0 -12.485,5.463 -12.485,12.201c0,3.118 5.324,12.063 7.134,15.251c2.198,3.869
            5.351,10.894 5.351,10.894c0,0 3.153,-7.025 5.351,-10.894c1.81,-3.188 7.134,-12.133 7.134,-15.251c0,-6.738
            -5.59,-12.201 -12.485,-12.201Z`,
            fillColor: "#3C4652",
            fillOpacity: 1,
            anchor: new google.maps.Point(10, 29),
            strokeWeight: 2,
            strokeColor: "#ffffff",
            scale: 1,
            labelOrigin: new google.maps.Point(14, 14),
        },
        label: {
            text: `${getStoreNumber(stores, store)}`,
            color: "#fff",
            fontSize: "12px",
            fontWeight: "light",
        },
    };
};

const createMapOptions = (): google.maps.MapOptions => {
    return {
        scrollwheel: false,
        draggable: true,
        disableDefaultUI: false,
        clickableIcons: false,
        mapTypeId: "roadmap",
    };
};

export const LocatorNearestStores = (props: {
    stores: ReadonlyArray<IRetailStoreWithDistance>;
    focusedStoreID: ISyncStoreID | null;
    onSelectStore: (storeID: ISyncStoreID | null) => void;
    showMore: boolean;
    disableGrouping?: boolean;
    noResultsText?: string;
}): React.ReactNode => {
    const mapElem = React.useRef<MarkerMapHandle>(null);

    const handleBeforeInfoWindowOpen = (
        info: google.maps.InfoWindow,
        store: IRetailStoreWithDistance,
    ) => {
        const content = createInfoWindowContent(store);
        info.setContent(content);
        props.onSelectStore(store.external_id);
    };

    const handleInfoWindowClose = () => {
        props.onSelectStore(null);
    };

    const getStoreNumberForStore = (store: IRetailStoreWithDistance) => {
        return getStoreNumber(props.stores, store);
    };

    const groups = props.disableGrouping
        ? props.stores.map((store) => {
              return {
                  main: store,
                  secondary: [],
              };
          })
        : groupStores(props.stores);

    const renderRows = () => {
        if (groups.length <= 0) {
            return (
                <NoResults retailerName={props.noResultsText || t`stores`} />
            );
        }
        return groups.map((storeGroup) => {
            return (
                <LocatorNearestStoresRow
                    key={`${storeGroup.main.external_id}`}
                    store={storeGroup.main}
                    nestedStores={storeGroup.secondary}
                    selectedStoreID={props.focusedStoreID}
                    getStoreNumber={getStoreNumberForStore}
                    onSelectStore={(storeID, event) => {
                        event.stopPropagation();
                        mapElem.current?.onActivateMarker(storeID);
                        trackStoreAnalytics(storeGroup, storeID);
                    }}
                />
            );
        });
    };

    return (
        <>
            <div className="locations__map-wrapper">
                <div
                    className="locations__map"
                    role="region"
                    // TODO(i18n): Use t template tag for aria-label: aria-label={t`map`}
                    aria-label="map"
                >
                    <MarkerMap
                        ref={mapElem}
                        stores={props.stores}
                        markerOptions={(store: IRetailStoreWithDistance) => {
                            return createMarkerOptions(props.stores, store);
                        }}
                        elemProps={{
                            id: "locations__map",
                            className: "bh-sl-map",
                        }}
                        mapOptions={createMapOptions}
                        onBeforeInfoWindowOpen={handleBeforeInfoWindowOpen}
                        onInfoWindowClose={handleInfoWindowClose}
                    />
                    <div className="locations__extras">
                        <div className="send">
                            <LocatorSendResultsEmail
                                stores={props.stores}
                                getStoreNumber={getStoreNumberForStore}
                            />
                            <LocatorSendResultsPrint
                                stores={props.stores}
                                getStoreNumber={getStoreNumberForStore}
                            />
                            <LocatorSendResultsText
                                stores={props.stores}
                                getStoreNumber={getStoreNumberForStore}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <section className="locations__list-wrapper">
                <ul className="locations__list list">{renderRows()}</ul>
                {props.showMore && (
                    <div>
                        <a href="#all">{t`See More Results`}</a>
                    </div>
                )}
            </section>
        </>
    );
};
